import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Loader2, Clock } from 'lucide-react';

const computeDuration = (start, end) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if ([sh, sm, eh, em].some(Number.isNaN)) return null;
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff : null;
};

const formatDuration = (mins) => {
  if (!mins) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h} Std ${m} Min`;
  if (h > 0) return `${h} Std`;
  return `${m} Min`;
};

export const LocationTimeDialog = ({
  open,
  onOpenChange,
  locations,
  categories,
  selectedLocation,
  setSelectedLocation,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  category,
  setCategory,
  locationNotes,
  setLocationNotes,
  onSave,
  saving,
}) => {
  const duration = computeDuration(startTime, endTime);
  const canSave = selectedLocation && startTime && endTime && category && duration && duration > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Tätigkeit für anderen Standort</DialogTitle>
          <DialogDescription>
            Dokumentieren Sie ein Zeitfenster, in dem Sie während Ihrer Arbeitszeit für einen anderen Standort tätig waren.
            Dies wird als Nachweis gespeichert und nicht zur Arbeitszeit addiert.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="location-select">Standort *</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger id="location-select" data-testid="location-select">
                <SelectValue placeholder="Standort wählen" />
              </SelectTrigger>
              <SelectContent>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="start-time-input">Von *</Label>
              <Input
                id="start-time-input"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                data-testid="start-time-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end-time-input">Bis *</Label>
              <Input
                id="end-time-input"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                data-testid="end-time-input"
              />
            </div>
          </div>
          {duration > 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="duration-display">
              <Clock className="h-4 w-4" />
              Dauer: <span className="font-medium text-foreground">{formatDuration(duration)}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="category-select">Tätigkeit *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="category-select" data-testid="category-select">
                <SelectValue placeholder="Tätigkeit wählen" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes-input">Notiz (optional)</Label>
            <Input
              id="notes-input"
              value={locationNotes}
              onChange={(e) => setLocationNotes(e.target.value)}
              placeholder="z.B. Telefonat mit Standort X"
              data-testid="notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !canSave}
            data-testid="save-location-time-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
